import { getSessionCookie, getLanguageCookie } from './cookie-utility';
import ViaNetworkError from './error/ViaNetworkError';


export const SESSION_ID_PARAM_KEY = 'session-id';

/**
 * Appends query params to a path.
 *
 * Based on: https://github.com/github/fetch/issues/256#issuecomment-304198513
 * @param {string} path A URL path.
 * @param {object} params A simple object with {key, value} pairs that represent query params.
 */
export function createPathWithParams(path, params) {
  if (!params || Object.keys(params).length === 0) return path;

  const urlParams = new URLSearchParams(Object.entries(params));
  return `${path}?${urlParams}`;
}

/**
 * Checks if a url is relative or absolute.
 *
 * Taken from: https://stackoverflow.com/a/38979205/4480526
 * @param {string} url The url to be checked.
 */
export function isUrlAbsolute(url) {
  if (url.indexOf('//') === 0) return true; // URL is protocol-relative (= absolute)
  if (url.indexOf('://') === -1) return false; // URL has no protocol (= relative)
  if (url.indexOf('.') === -1) return false; // URL does not contain a dot, i.e. no TLD (= relative, possibly REST)
  if (url.indexOf('/') === -1) return false; // URL does not contain a single slash (= relative)
  if (url.indexOf(':') > url.indexOf('/')) return false; // The first colon comes after the first slash (= relative)
  if (url.indexOf('://') < url.indexOf('.')) return true; // Protocol is defined before first dot (= absolute)
  return false; // Anything else must be relative
}

/**
 * Converts a relative url to include the base via path.
 * @param {string} location The relative url.
 */
export const asViaUrl = location => `${process.env.PUBLIC_URL}${location}`;

/**
 * Converts a relative url to include the base via path and the session cookie.
 * @param {string} location The relative url.
 */
export const asViaUrlWithSession = location => `${process.env.PUBLIC_URL}${location}?${SESSION_ID_PARAM_KEY}=${getSessionCookie()}`;

/**
 * Handles the response body of a VIA response.
 * @param {object} response The fetch response from a VIA API
 */
function handleViaResponseBody(response) {
  // 204 (No Content) will never have a body.
  if (response.status === 204) return new Promise(resolve => resolve());

  // 404 (Not Found) gives an HTML page. We can ignore this in the context of fetching data.
  if (response.status === 404) return new Promise(resolve => resolve());

  // Check the content type.
  const contentType = response.headers.get('content-type');
  if (!contentType) return new Promise(resolve => resolve());

  // Check the content length. This may not be included, even when there is content.
  let contentLength = response.headers.get('content-length');
  contentLength = contentLength ? Number(contentLength) : null;
  if (contentLength != null && contentLength === 0) return new Promise(resolve => resolve());

  // Handle the different types of formats. Will need to be handled case by case.
  if (contentType.startsWith('image/svg+xml')) return response.text();
  if (contentType.startsWith('text/plain')) return response.text();
  if (contentType.startsWith('application/json')) return response.json();
  if (contentType.startsWith('application/octet-stream')) return response.blob();
  if (contentType.startsWith('application/pdf')) return response.blob();


  // Content type hasn't been handled. Warn the developer and return nothing.
  console.log(`Via: Unknown content type: ${contentType}`); // eslint-disable-line no-console
  return new Promise(resolve => resolve());
}

/**
 * @param {string} method The request method for the http call. see: https://developer.mozilla.org/en-US/docs/Web/HTTP/Methods
 * @param {location} location The location for the http call.
 * @param {object} [queryParams] (optional) Additional query parameters: { key1: value1, key2: ... }
 * @param {object} [body] (optional) Additional body of the request.
 * @returns {promise} A promise with the response data or an error.
 */
export async function request(method, location, queryParams, body) {
  const params = queryParams || {};


  // We always pass up the session-id as a query parameter to prevent CSRF.
  params[SESSION_ID_PARAM_KEY] = getSessionCookie();

  // Add params to the path. If the path is not absolute then prefix it with our own base path.
  const path = createPathWithParams(isUrlAbsolute(location) ? location : asViaUrl(location), params);

  const requestOptions = {
    method,
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      'accept-language': getLanguageCookie(),
    },
  };

  const response = await fetch(path, requestOptions);
  const responseData = handleViaResponseBody(response);
  if (!response.ok) {
    const errorData = await responseData;
    const errorMessage = errorData ? errorData.errorMessage : '';
    throw new ViaNetworkError(errorMessage, response.status, errorData);
  }
  return responseData;
}


/**
 * Creates an HTTP post and returns a promise.
 * This automatically sends up the session-id as a cookie and query parameter.
 *
 * @param {string} url The url of the request.
 * @param {object} [params] (optional) Additional query parameters: { key1: value1, key2: value2 }
 * @param {object} [body] The body of the request in JSON format.
 * @example
 * post('/api/example/1', { param1: 'value1', param2: 'value2' }, { description: 'example' })
 *  .then(response => { console.log(`success: ${response}`) })
 *  .catch(error => { console.log(`failed: ${error}` )});
 */
export function post(url, params, body) {
  return request('POST', url, params, body);
}

/**
 * Creates an HTTP post and returns a promise.
 * This automatically sends up the session-id as a cookie and query parameter.
 *
 * @param {string} url The url of the request.
 * @param {object} [params] (optional) Additional query parameters: { key1: value1, key2: value2 }
 * @param {object} [body] The body of the request in JSON format.
 * @example
 * put('/api/example/1', { param1: 'value1', param2: 'value2' }, { description: 'example' })
 *  .then(response => { console.log(`success: ${response}`) })
 *  .catch(error => { console.log(`failed: ${error}` )});
 */
export function put(url, params, body) {
  return request('PUT', url, params, body);
}

/**
 * Creates an HTTP post and returns a promise.
 * This automatically sends up the session-id as a cookie and query parameter.
 *
 * @example
 * get('/api/example', { param1: 'value1', param2: 'value2' })
 *  .then(response => { console.log(`success: ${response}`) })
 *  .catch(error => { console.log(`failed: ${error}` )});
 *
 * @param {string} url The url of the request.
 * @param {object} [params] (optional) Additional query parameters: { key1: value1, key2: value2 }
 */
export function remove(url, params) {
  return request('DELETE', url, params);
}

/**
 * Creates an HTTP post and returns a promise.
 * This automatically sends up the session-id as a cookie and query parameter.
 *
 * @example
 * get('/api/example', { param1: 'value1', param2: 'value2' })
 *  .then(response => { console.log(`success: ${response}`) })
 *  .catch(error => { console.log(`failed: ${error}` )});
 *
 * @param {string} url The url of the request.
 * @param {object} [params] (optional) Additional query parameters: { key1: value1, key2: value2 }
 */
export function get(url, params) {
  return request('GET', url, params);
}

/**
 * External Get (user not logged in)
 * @param {string} url The location for the http call.
 * @param {object} [queryParams] (optional) Additional query parameters: { key1: value1, key2: ... }
 * @param {object} [body] (optional) Additional body of the request.
 * @returns {promise} A promise with the response data or an error.
 */
export async function externalGet(url, queryParams, body) {
  const params = queryParams || {};

  // Add params to the path. If the path is not absolute then prefix it with our own base path.
  const path = createPathWithParams(isUrlAbsolute(url) ? url : asViaUrl(url), params);

  const requestOptions = {
    method: 'GET',
    body: body ? JSON.stringify(body) : undefined,
    headers: {
      'content-type': 'application/json',
    },
  };

  const response = await fetch(path, requestOptions);
  const responseData = handleViaResponseBody(response);

  if (!response.ok) {
    const errorData = await responseData;
    const errorMessage = errorData ? errorData.errorMessage : '';
    throw new ViaNetworkError(errorMessage, response.status, errorData);
  }
  return responseData;
}
